import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Logo from "../../components/ui/Logo";
import { BILLING_PLANS } from "../../config/billingPlans";
import SampleProof from "../../components/marketing/SampleProof";

// The 6 Ontario Learning Skills, mandated by the Growing Success framework.
const LEARNING_SKILLS = [
  "Responsibility",
  "Organization",
  "Independent Work",
  "Collaboration",
  "Initiative",
  "Self-Regulation",
];

const TESTIMONIALS = [
  {
    quote:
      "Wow! This is amazing and so much easier to use than student evaluator.",
    name: "Sheryl",
    attribution: "Elementary Teacher, Ontario",
  },
  {
    quote:
      "I used to dread report card season. This AI tool has made a world of difference.",
    name: "Leland",
    attribution: "Elementary Teacher, Ontario",
  },
  {
    quote: "Wow. You weren't kidding. This is exactly what I'd dreamt of!",
    name: "moose_ranger",
    attribution: "Verified user, Reddit",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Pick the student's levels",
    body: "Slide E / G / S / N for each of the 6 Ontario Learning Skills.",
  },
  {
    n: "02",
    title: "Add a few notes",
    body: "Drop in observations, or paste a comment you wrote earlier so it learns your voice.",
  },
  {
    n: "03",
    title: "Get a comment in your voice",
    body: "A clean, Ontario-ready draft in seconds. Edit, copy, move on.",
  },
];

// Keep the props shape so existing App.tsx routes (`/ontario`,
// `/ontario/learning-skills`) keep compiling. Per ADR 0009 we are Ontario-first
// and the page no longer branches on these. Every visitor lands on the same
// focused Ontario page.
type LandingPageProps = {
  forcedRegionCode?: string;
  focus?: "DEFAULT" | "ONTARIO_LEARNING_SKILLS";
};

export default function LandingPage(_props: LandingPageProps = {}) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-stone-50 font-sans text-stone-900 selection:bg-brand-100 selection:text-brand-900">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-3 focus:py-2 focus:bg-brand-700 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <Nav />
      <main id="main" tabIndex={-1}>
        <Hero />
        <TrustStrip />
        <HowItWorks />
        {/* E48 cont'd: the proof section also goes on the Ontario landing.
            HowItWorks tells teachers "three steps, less than a minute" in
            words. SampleProof shows the actual output (Maya + Liam) so
            they SEE what comes out before clicking. */}
        <SampleProof />
        <Moat />
        <Pricing />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <nav aria-label="Site" className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
      <div className="h-16 px-4 sm:px-6 lg:px-10 flex items-center gap-4 max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-3 min-w-0">
          <Logo className="w-9 h-9 flex-shrink-0" aria-hidden="true" />
          <span className="text-lg font-bold text-stone-900 truncate">
            TeacherDashboard
          </span>
        </Link>

        <div className="hidden md:flex items-center justify-center gap-8 flex-1">
          <a
            href="#how"
            className="text-sm font-medium text-stone-600 hover:text-brand-700 transition-colors"
          >
            How it works
          </a>
          <a
            href="#why"
            className="text-sm font-medium text-stone-600 hover:text-brand-700 transition-colors"
          >
            Why TeacherDashboard
          </a>
          <a
            href="#pricing"
            className="text-sm font-medium text-stone-600 hover:text-brand-700 transition-colors"
          >
            Pricing
          </a>
        </div>

        <div className="flex items-center gap-3 ml-auto">
          <Link
            to="/login"
            className="hidden md:block text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="px-4 py-2 bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
          >
            Try it free
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
      {/* Subtle ambient violet glow, no stock illustration. */}
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_15%,rgba(77,56,115,0.12),transparent_55%),radial-gradient(circle_at_85%_10%,rgba(245,158,11,0.10),transparent_50%)]" />

      <div className="px-4 sm:px-6 lg:px-10 max-w-5xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/70 backdrop-blur-sm border border-stone-200 rounded-full text-stone-700 text-xs font-semibold uppercase tracking-[0.12em] mb-7 shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-700" />
          Built for Ontario teachers, K through 8
        </div>

        <h1 className="font-sans text-4xl md:text-6xl font-bold tracking-tight text-stone-900 leading-[1.15] mb-8">
          Report card comments
          <br />
          in <span className="text-brand-700">your voice</span>. In seconds.
        </h1>

        <p className="text-lg md:text-xl text-stone-600 max-w-2xl mx-auto mb-10 leading-relaxed">
          Built around the 6 Ontario Learning Skills and the E / G / S / N
          scale. TeacherDashboard knows how Ontario teachers actually write.
          ChatGPT does not.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5">
          <Link
            to="/signup"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 bg-brand-700 hover:bg-brand-800 text-white font-semibold rounded-xl text-base transition-all shadow-lg shadow-brand-600/20 hover:shadow-xl hover:-translate-y-0.5"
          >
            Try it free
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
        <p className="text-sm text-stone-500">
          Free to start. Sign up with Google or email.
        </p>

        {/* Typographic hero panel: a stylized preview of the actual tool. */}
        <div className="mt-14 max-w-3xl mx-auto">
          <div className="rounded-2xl border border-stone-200 bg-white shadow-xl shadow-brand-600/5 overflow-hidden text-left">
            <div className="px-5 py-3 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-stone-200" />
                <span className="w-2.5 h-2.5 rounded-full bg-stone-200" />
                <span className="w-2.5 h-2.5 rounded-full bg-stone-200" />
              </div>
              <div className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">
                Learning Skills · Ontario
              </div>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-3">
                {[
                  { label: "Responsibility", level: "G" },
                  { label: "Collaboration", level: "E" },
                  { label: "Self-Regulation", level: "S" },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between rounded-lg border border-stone-200 bg-stone-50 px-3 py-2"
                  >
                    <span className="text-sm font-medium text-stone-700">
                      {row.label}
                    </span>
                    <div className="flex gap-1">
                      {/* Order N → S → G → E so the scale reads low-to-high
                          (matches the actual LS slider in-product). */}
                      {["N", "S", "G", "E"].map((l) => (
                        <span
                          key={l}
                          className={`w-7 h-7 inline-flex items-center justify-center rounded-md text-xs font-bold ${
                            l === row.level
                              ? "bg-brand-700 text-white"
                              : "bg-white text-stone-400 border border-stone-200"
                          }`}
                        >
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-800 mb-2">
                  Generated comment
                </div>
                <p className="font-serif text-[15px] text-stone-800 leading-relaxed">
                  Maya consistently completes assigned tasks on time and takes
                  pride in her work. She thrives in group settings and lifts
                  her peers' contributions. Next step: continue building
                  strategies to manage frustration when challenges arise.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="px-4 sm:px-6 lg:px-10 py-16 bg-white border-y border-stone-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-800 mb-2">
            Loved by Ontario teachers
          </p>
          <h2 className="font-sans text-2xl md:text-3xl font-bold text-stone-900">
            Built for the Ontario Growing Success framework
          </h2>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {LEARNING_SKILLS.map((skill) => (
              <span
                key={skill}
                className="px-3 py-1 rounded-full bg-stone-50 border border-stone-200 text-sm text-stone-700"
              >
                {skill}
              </span>
            ))}
            <span className="px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-sm font-semibold text-amber-700">
              Scored E / G / S / N
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {TESTIMONIALS.map((t) => (
            <figure
              key={t.name}
              className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm flex flex-col"
            >
              <blockquote className="font-serif text-stone-800 text-lg leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-stone-100">
                <div className="text-sm font-semibold text-stone-900">
                  {t.name}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">
                  {t.attribution}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section
      id="how"
      className="scroll-mt-24 px-4 sm:px-6 lg:px-10 py-20 bg-stone-50"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-sans text-3xl md:text-4xl font-bold text-stone-900 mb-3">
            How it works
          </h2>
          <p className="text-stone-600 text-lg">
            Three steps. Less than a minute per student.
          </p>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {STEPS.map((step) => (
            <li
              key={step.n}
              className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm"
            >
              <div className="text-xs font-bold tracking-[0.18em] text-brand-700 mb-3">
                STEP {step.n}
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                {step.title}
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Moat() {
  const knows = [
    "The 6 Ontario Learning Skills, in order, with the indicators teachers actually report on",
    "That Ontario uses E / G / S / N, not A through D, not 1 through 4",
    "Ontario report card phrasing conventions: strengths, next steps, evidence",
    "How Term 1 differs from Term 2, and how Kindergarten Communication of Learning differs from Grades 1 through 8",
  ];

  const doesnt = [
    "ChatGPT defaults to American grading language and A through D",
    "MagicSchool writes generic comments that don't match how Ontario reports",
    "Generic AI tools don't know what 'Independent Work' means as a Learning Skill",
    "Both will happily invent indicators that aren't in the Ontario framework",
  ];

  return (
    <section
      id="why"
      className="scroll-mt-24 px-4 sm:px-6 lg:px-10 py-20 bg-white"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-800 mb-3">
            Tailored to Ontario report cards
          </p>
          <h2 className="font-sans text-3xl md:text-4xl font-bold text-stone-900 mb-4">
            Learning Skills comments built around E / G / S / N.
          </h2>
          <p className="text-stone-600 text-lg">
            Choose the student, level, and strengths. TeacherDashboard drafts a
            clear Ontario-style comment using the right Learning Skills language,
            so you can review instead of starting from scratch.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
          <div className="rounded-2xl border-2 border-brand-200 bg-gradient-to-br from-brand-50 to-white p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-brand-700 text-white flex items-center justify-center">
                <Logo className="w-6 h-6" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-stone-900">
                What TeacherDashboard knows
              </h3>
            </div>
            <ul className="space-y-3">
              {knows.map((line) => (
                <li key={line} className="flex items-start gap-3 text-sm">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span className="text-stone-700 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-stone-50 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-stone-200 text-stone-500 flex items-center justify-center font-bold">
                ?
              </div>
              <h3 className="text-lg font-bold text-stone-900">
                What you should not have to explain
              </h3>
            </div>
            <ul className="space-y-3">
              {doesnt.map((line) => (
                <li key={line} className="flex items-start gap-3 text-sm">
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-stone-300 text-white text-[10px] font-bold">
                    ×
                  </span>
                  <span className="text-stone-600 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section
      id="pricing"
      className="scroll-mt-24 px-4 sm:px-6 lg:px-10 py-20 bg-stone-50"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-sans text-3xl md:text-4xl font-bold text-stone-900 mb-3">
            Less than a coffee a month
          </h2>
          <p className="text-stone-600 text-lg">
            Same Pro features either way. Pick the cadence that fits your
            budget.
          </p>
          <p className="mt-4 text-sm font-medium text-stone-700">
            3-day free trial · 30-day money-back guarantee · Cancel anytime
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
          {BILLING_PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-2xl border p-6 shadow-sm flex flex-col ${
                plan.isRecommended
                  ? "border-brand-300 bg-white ring-2 ring-brand-200"
                  : "border-stone-200 bg-white"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-stone-900">
                  {plan.name}
                </h3>
                {plan.badge && (
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      plan.isRecommended
                        ? "bg-brand-700 text-white"
                        : "bg-stone-100 text-stone-600"
                    }`}
                  >
                    {plan.badge}
                  </span>
                )}
              </div>

              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold tracking-tight text-stone-900">
                  {plan.monthlyPriceLabel}
                </span>
                <span className="text-base font-semibold text-stone-500">
                  / month
                </span>
              </div>
              <p className="text-sm text-stone-600">{plan.billingLabel}</p>
              <p className="mt-1 text-xs text-stone-500">
                {plan.billedAmountLabel}
              </p>

              {plan.savingsLabel && (
                <div className="mt-3 inline-flex self-start rounded-md bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                  {plan.savingsLabel}
                </div>
              )}

              <ul className="mt-5 space-y-2 text-sm text-stone-700 flex-1">
                {[
                  "Unlimited Learning Skills comments",
                  "Unlimited subject comments + rubrics",
                  "Saved drafts and editable history",
                  "Save and reuse drafts",
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-brand-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  plan.isRecommended
                    ? "bg-brand-700 hover:bg-brand-800 text-white"
                    : "bg-stone-900 hover:bg-stone-800 text-white"
                }`}
              >
                Start free trial
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-6 text-center text-sm text-stone-500">
          School or board licensing? Email{" "}
          <a
            href="mailto:hello@teacherdashboard.ai"
            className="font-semibold text-stone-700 hover:text-stone-900"
          >
            hello@teacherdashboard.ai
          </a>
          .
        </p>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="px-4 sm:px-6 lg:px-10 py-20 bg-white">
      <div className="max-w-3xl mx-auto rounded-3xl bg-gradient-to-br from-brand-700 to-brand-800 text-white text-center p-10 md:p-14 shadow-2xl shadow-brand-600/20">
        <h2 className="font-sans text-3xl md:text-5xl font-bold mb-4 leading-[1.1]">
          Stop dreading report cards.
        </h2>
        <p className="text-lg text-brand-100 mb-8 max-w-xl mx-auto">
          Join the Ontario teachers writing reports in their voice. In
          seconds, not weekends.
        </p>
        <Link
          to="/signup"
          className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-brand-800 font-bold rounded-xl text-base hover:bg-brand-50 transition-colors shadow-lg"
        >
          Try it free
          <ArrowRight className="w-5 h-5" aria-hidden="true" />
        </Link>
        <p className="mt-4 text-sm text-brand-200">
          Free to start. 3 free generations.
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="px-4 sm:px-6 lg:px-10 py-10 bg-white border-t border-stone-100">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2">
          <Logo className="w-7 h-7" aria-hidden="true" />
          <span className="text-sm font-bold text-stone-900">
            TeacherDashboard
          </span>
        </Link>
        <div className="flex gap-6 text-sm text-stone-500">
          <a href="#why" className="hover:text-stone-900">
            Why us
          </a>
          <a href="#pricing" className="hover:text-stone-900">
            Pricing
          </a>
          <Link to="/login" className="hover:text-stone-900">
            Sign in
          </Link>
          <a
            href="mailto:hello@teacherdashboard.ai"
            className="hover:text-stone-900"
          >
            Contact
          </a>
        </div>
        <div className="text-xs text-stone-500">
          © 2026 TeacherDashboard · Built for Ontario teachers, K through 8
        </div>
      </div>
    </footer>
  );
}
